import { ArrowLeft, ArrowRight, ListChecks, Plus, RefreshCw, Scissors } from "lucide-react";
import { BOARD_COUNT, BOARD_SECONDS, SURFACE } from "./constants";
import {
  SCRIPT_UNIT_TYPE_META,
  buildManualStoryboardSegments,
  normalizeManualSegments,
} from "./utils";

const cloneSegments = (segments) => (
  (segments || []).map((segment) => ({
    ...segment,
    beats: Array.isArray(segment.beats) ? [...segment.beats] : [],
  }))
);

const SmallButton = ({ children, icon: Icon, onClick, disabled, title, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`${compact ? "h-7 px-1.5" : "h-8 px-2"} rounded-md border text-xs font-semibold inline-flex items-center gap-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed`}
    style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
  >
    {Icon ? <Icon size={13} /> : null}
    {children}
  </button>
);

const UnitChip = ({ unit }) => {
  const meta = SCRIPT_UNIT_TYPE_META[unit?.type] || SCRIPT_UNIT_TYPE_META.note;
  return (
    <div className="rounded-md border px-2 py-1.5 text-xs leading-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.page }}>
      <span className="inline-flex items-center h-5 px-1.5 rounded mr-1 font-bold" style={{ color: meta.color, backgroundColor: meta.colorSoft }}>
        {meta.label}
      </span>
      {unit?.speaker ? <span className="font-bold mr-1" style={{ color: "var(--color-text-primary)" }}>{unit.speaker}：</span> : null}
      <span style={{ color: "var(--color-text-secondary)" }}>{unit?.text || unit?.rawText}</span>
    </div>
  );
};

const ManualSegmentationPanel = ({
  scriptText,
  mode,
  onModeChange,
  presentationMode = "formal",
  segments,
  onSegmentsChange,
  disabled,
  duration = BOARD_SECONDS,
  hideHeader = false,
}) => {
  const normalizedSegments = normalizeManualSegments(segments, duration);
  const totalSpoken = normalizedSegments.reduce((sum, segment) => sum + (Number(segment.spokenCharCount) || 0), 0);
  const totalUnits = normalizedSegments.reduce((sum, segment) => sum + (segment.beats?.length || 0), 0);
  const totalSeconds = normalizedSegments.length * duration;
  const compact = hideHeader;

  const resetSegments = () => {
    onSegmentsChange(buildManualStoryboardSegments(scriptText, BOARD_COUNT, duration, presentationMode));
  };

  const updateSegments = (recipe) => {
    onSegmentsChange(normalizeManualSegments(recipe(cloneSegments(normalizedSegments)), duration));
  };

  const splitSegment = (index) => {
    updateSegments((next) => {
      const segment = next[index];
      if (!segment || segment.beats.length < 2 || next.length >= 24) return next;
      const splitAt = Math.ceil(segment.beats.length / 2);
      const tail = segment.beats.splice(splitAt);
      next.splice(index + 1, 0, {
        index: index + 2,
        title: `第${index + 2}张故事板`,
        beats: tail,
      });
      return next;
    });
  };

  const mergeNext = (index) => {
    updateSegments((next) => {
      if (next.length <= BOARD_COUNT) return next;
      if (!next[index + 1]) return next;
      next[index].beats.push(...next[index + 1].beats);
      next.splice(index + 1, 1);
      return next;
    });
  };

  const pushLastToNext = (index) => {
    updateSegments((next) => {
      if (!next[index]?.beats?.length) return next;
      if (!next[index + 1]) {
        if (next.length >= 24) return next;
        next.push({ index: next.length + 1, title: `第${next.length + 1}张故事板`, beats: [] });
      }
      const moved = next[index].beats.pop();
      if (moved) next[index + 1].beats.unshift(moved);
      return next;
    });
  };

  const pullFirstFromNext = (index) => {
    updateSegments((next) => {
      if (!next[index + 1]?.beats?.length) return next;
      const moved = next[index + 1].beats.shift();
      if (moved) next[index].beats.push(moved);
      return next;
    });
  };

  if (compact && mode === "manual") {
    return (
      <section
        className="shrink-0 rounded-xl border px-3 py-2.5 space-y-2"
        style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex items-center gap-2">
            <span
              className="h-8 w-8 shrink-0 rounded-md border inline-flex items-center justify-center"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-accent-primary)" }}
            >
              <Scissors size={15} />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>手动拆段</div>
              <div className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>
                {normalizedSegments.length} 张 · {totalUnits} 个剧本单元 · {presentationMode === "narration" ? "口播/对白" : "可读台词/OS"} {totalSpoken} 字 · 约 {totalSeconds}s
              </div>
            </div>
          </div>
          <SmallButton icon={RefreshCw} onClick={resetSegments} disabled={disabled || !scriptText} title="重新按完整句和40字目标拆分" compact>
            重置
          </SmallButton>
        </div>

        {normalizedSegments.length ? (
          <div className="flex gap-2 overflow-x-auto overflow-y-hidden custom-scrollbar pb-1">
            {normalizedSegments.map((segment, index) => (
              <div
                key={`manual-segment-compact-${segment.index}-${index}`}
                className="shrink-0 w-[292px] rounded-lg border p-2 space-y-2"
                style={{ borderColor: segment.warning ? "rgba(245,158,11,0.55)" : "var(--color-border-primary)", backgroundColor: SURFACE.card }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-black truncate" style={{ color: "var(--color-text-primary)" }}>
                      #{String(segment.index).padStart(2, "0")} · {segment.timeRange}
                    </div>
                    <div className="text-[11px] mt-0.5 truncate" style={{ color: segment.warning ? "#f59e0b" : "var(--color-text-muted)" }}>
                      对白 {segment.dialogueCount} · OS {segment.osCount} · 画面 {segment.visualCount} · {segment.spokenCharCount} 字
                    </div>
                  </div>
                  {segment.warning && (
                    <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: "color-mix(in srgb, var(--color-warning) 14%, transparent)", color: "var(--color-warning)" }}>
                      提醒
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-1">
                  <SmallButton icon={Scissors} onClick={() => splitSegment(index)} disabled={disabled || segment.beats.length < 2 || normalizedSegments.length >= 24} title="把当前段拆成两张故事板" compact>
                    拆
                  </SmallButton>
                  <SmallButton icon={ArrowRight} onClick={() => pushLastToNext(index)} disabled={disabled || segment.beats.length < 2} title="把当前段最后一个完整剧本单元移到下一张" compact>
                    后
                  </SmallButton>
                  <SmallButton icon={ArrowLeft} onClick={() => pullFirstFromNext(index)} disabled={disabled || !normalizedSegments[index + 1]?.beats?.length} title="从下一张拿第一个完整剧本单元" compact>
                    前
                  </SmallButton>
                  <SmallButton icon={Plus} onClick={() => mergeNext(index)} disabled={disabled || normalizedSegments.length <= BOARD_COUNT || !normalizedSegments[index + 1]} title="把下一张合并进当前张" compact>
                    合
                  </SmallButton>
                </div>

                {segment.warning && (
                  <div className="rounded-md border px-2 py-1 text-[11px] leading-4" style={{ borderColor: "color-mix(in srgb, var(--color-warning) 50%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-warning) 12%, transparent)", color: "var(--color-warning)" }}>
                    {segment.warning}
                  </div>
                )}

                <div className="space-y-1 max-h-[82px] overflow-y-auto custom-scrollbar pr-1">
                  {segment.beats.slice(0, 4).map((unit) => (
                    <UnitChip key={unit.id} unit={unit} />
                  ))}
                  {segment.beats.length > 4 && (
                    <div className="text-[11px] px-2 py-1" style={{ color: "var(--color-text-muted)" }}>
                      还有 {segment.beats.length - 4} 个单元
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-md border border-dashed p-4 text-center text-sm" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
            <ListChecks size={22} className="mx-auto mb-2" />
            当前集还没有可拆分剧本。
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="shrink-0 rounded-md border p-4 space-y-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
      {!hideHeader && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-bold inline-flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
              <Scissors size={17} />
              剧本拆段方式
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--color-text-muted)" }}>
              自动拆段适合快速出图；手动拆段会严格按你确认的段落生成，不会把一句对白或一段画面描述从中间切开。
            </div>
          </div>
          <div className="inline-flex rounded-md border p-1 shrink-0" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
            {[
              ["ai", "AI自动拆段"],
              ["manual", "手动拆段"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => onModeChange(value)}
                disabled={disabled}
                className="h-8 px-3 rounded text-sm font-semibold cursor-pointer disabled:opacity-50"
                style={{
                  backgroundColor: mode === value ? "var(--color-accent-primary)" : "transparent",
                  color: mode === value ? "#fff" : "var(--color-text-secondary)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === "manual" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
            <div className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
              已拆 <b style={{ color: "var(--color-text-primary)" }}>{normalizedSegments.length}</b> 张故事板，
              共 <b style={{ color: "var(--color-text-primary)" }}>{totalUnits}</b> 个剧本单元，
              {presentationMode === "narration" ? "口播/对白" : "可读台词/OS"} 约 <b style={{ color: "var(--color-text-primary)" }}>{totalSpoken}</b> 字，
              目标时长约 <b style={{ color: "var(--color-text-primary)" }}>{totalSeconds}</b> 秒。
            </div>
            <SmallButton icon={RefreshCw} onClick={resetSegments} disabled={disabled || !scriptText} title="重新按完整句和40字目标拆分">
              重置拆段
            </SmallButton>
          </div>

          <div className="grid grid-cols-1 min-[1700px]:grid-cols-2 gap-3">
            {normalizedSegments.map((segment, index) => (
              <div
                key={`manual-segment-${segment.index}-${index}`}
                className="rounded-md border p-3 space-y-3"
                style={{ borderColor: segment.warning ? "rgba(245,158,11,0.55)" : "var(--color-border-primary)", backgroundColor: SURFACE.card }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>
                      第{segment.index}张故事板 · {segment.timeRange}
                    </div>
                    <div className="text-xs mt-1" style={{ color: segment.warning ? "#f59e0b" : "var(--color-text-muted)" }}>
                      {segment.dialogueCount} 段对白 · {segment.osCount} 段内心OS · {segment.visualCount} 段画面 · 可读 {segment.spokenCharCount} 字
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1">
                    <SmallButton icon={Scissors} onClick={() => splitSegment(index)} disabled={disabled || segment.beats.length < 2 || normalizedSegments.length >= 24} title="把当前段拆成两张故事板">
                      拆开
                    </SmallButton>
                    <SmallButton icon={ArrowRight} onClick={() => pushLastToNext(index)} disabled={disabled || segment.beats.length < 2} title="把当前段最后一个完整剧本单元移到下一张">
                      后移
                    </SmallButton>
                    <SmallButton icon={ArrowLeft} onClick={() => pullFirstFromNext(index)} disabled={disabled || !normalizedSegments[index + 1]?.beats?.length} title="从下一张拿第一个完整剧本单元">
                      前移
                    </SmallButton>
                    <SmallButton icon={Plus} onClick={() => mergeNext(index)} disabled={disabled || normalizedSegments.length <= BOARD_COUNT || !normalizedSegments[index + 1]} title="把下一张合并进当前张">
                      合并
                    </SmallButton>
                  </div>
                </div>

                {segment.warning && (
                  <div className="rounded-md border px-2 py-1 text-xs" style={{ borderColor: "color-mix(in srgb, var(--color-warning) 50%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-warning) 12%, transparent)", color: "var(--color-warning)" }}>
                    {segment.warning}
                  </div>
                )}

                <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                  {segment.beats.map((unit) => (
                    <UnitChip key={unit.id} unit={unit} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {!normalizedSegments.length && (
            <div className="rounded-md border border-dashed p-6 text-center" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
              <ListChecks size={24} className="mx-auto mb-2" />
              当前集还没有可拆分剧本。
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ManualSegmentationPanel;
